import React from 'react';
import dayjs from 'dayjs';
import { Box, Typography, Button, Paper, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { DATE_PRESETS } from './reportDatePresets';

/**
 * @param {{
 *   title: string,
 *   subtitle: string,
 *   datePreset: string,
 *   onDatePresetChange: (id: string) => void,
 *   dateFrom: import('dayjs').Dayjs,
 *   dateTo: import('dayjs').Dayjs,
 *   onDateFromChange: (v: import('dayjs').Dayjs | null) => void,
 *   onDateToChange: (v: import('dayjs').Dayjs | null) => void,
 *   onExport: () => void,
 *   exportDisabled?: boolean,
 *   extra?: React.ReactNode,
 * }} props
 */
export default function ReportPeriodToolbar({
  title,
  subtitle,
  datePreset,
  onDatePresetChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onExport,
  exportDisabled = false,
  extra = null,
}) {
  const showCustomPickers = datePreset === 'custom';

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" component="h1">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            {subtitle}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={onExport} disabled={exportDisabled}>
          Export CSV
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <ToggleButtonGroup
          value={datePreset}
          exclusive
          size="small"
          onChange={(_, v) => v != null && onDatePresetChange(v)}
          sx={{ flexWrap: 'wrap' }}
        >
          {DATE_PRESETS.map((p) => (
            <ToggleButton key={p.id} value={p.id} sx={{ textTransform: 'none' }}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {showCustomPickers && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From"
              value={dateFrom}
              onChange={onDateFromChange}
              slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
            />
            <DatePicker
              label="To"
              value={dateTo}
              onChange={onDateToChange}
              slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
            />
          </LocalizationProvider>
        )}

        {extra ? (
          <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1 }}>
            {extra}
          </Box>
        ) : null}
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {dayjs(dateFrom).isValid() && dayjs(dateTo).isValid()
          ? `${dayjs(dateFrom).format('MMM D, YYYY')} – ${dayjs(dateTo).format('MMM D, YYYY')}`
          : ''}
      </Typography>
    </Box>
  );
}
